import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, IconClear, IconHelp, IconOrderBottom, IconOrderDown, IconOrderTop, IconOrderUp, IconVerify } from "../../";
import { KEYBOARD_KEYS } from "../../common/constants";
import { useUniqueId } from "../../common/hooks";
import { mergeRefs } from "../../common/utilities";
import LiveRegion from "../../private/LiveRegion/LiveRegion";
import { ANNOUNCEMENT_TIMEOUT, CLEAR_ANNOUNCEMENT_TIMEOUT, EDIT, KEYBOARD_HELP_BTN_DESCRIPTION, KEYBOARD_HELP_BUTTON, REORDER, REORDERSAVE, REORDER_INSTRUCTIONS, SELECTION_MODE_DISABLED, SELECTION_MODE_ENABLED, TOP, UP } from "../constants";
import ListBuilderPanel from "../ListBuilderPanel/ListBuilderPanel";
import { handleTableCellFocus } from "../utils";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const FOCUS_TABLE_CELL = true;
/**
 * The ReorderPanel is a React component for reordering the Table row data in the
 * preferred sequence.
 */

const ReorderPanel = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    children,
    openReorderPanel,
    disableMoveUpTopButtons,
    disableMoveDownBottomButtons,
    reorderPanelData,
    elementToFocusOnClose,
    reorderTableName,
    customStrings,
    reorderSelectedRows,
    onClearSelections,
    onReorderSelect,
    onMoveTopBottom,
    onMoveUpDown,
    onEditOpen,
    afterSave,
    onClose,
    reorderPanelHeaderDockAriaLabel,
    reorderData,
    panelWidth,
    onHandleCloseKeyboardHelp,
    reorderPanelEditButtonHidden,
    disableButtons
  } = _ref;
  const theme = useTheme();
  const {
    cssRoot,
    cssButtonsContainer,
    cssKeyboardHelpButtonContainer,
    cssKeyboardHelpButton,
    cssSelectedItems,
    cssClearSelectionsButton,
    cssFooterButtons,
    cssCancelButton,
    cssEditButton,
    cssSaveButton,
    iconSize,
    helpIcon
  } = extractTheme({
    theme
  });
  const [liveText, setLiveText] = useState("");
  const uniqueId = useUniqueId();
  const reorderString = "pnr-reorder-panel";
  const moveToTopDescriptionId = `${reorderString}-move-to-top-desc-${uniqueId}`;
  const moveUpDescriptionId = `${reorderString}-move-up-desc-${uniqueId}`;
  const moveDownDescriptionId = `${reorderString}-move-down-desc-${uniqueId}`;
  const moveToBottomDescriptionId = `${reorderString}-move-to-bottom-desc-${uniqueId}`;
  const reorderTableCaptionId = `${reorderString}-table-caption-${uniqueId}`;
  const clearSelectionDescriptionId = `${reorderString}-clear-selection-desc-${uniqueId}`;
  const selectedCountId = `${reorderString}-selected-count-${uniqueId}`;
  const keyboardHelpButtonDescriptionId = `${reorderString}-help-button-desc-${uniqueId}`;
  /**
   * If the current activeElement is `topButton` and on click of that its state changed
   * to disabled, at that time if `bottomButton` is active then focus should go on to it.
   * This should happen only if click triggered on one of the reorder button.
   */

  const reorderButtonTriggeredRef = useRef(false);
  const elementToFocusOnHelpPanelCloseRef = useRef();
  const tableElementRef = useRef();
  const topButtonRef = useRef();
  const bottomButtonRef = useRef();
  const upButtonRef = useRef();
  const downButtonRef = useRef();
  const reorderPanelNodeRef = useRef();
  const keyBoardHelpButtonNodeRef = useRef();
  const firstFocusUpdateRef = useRef(true);
  const activeElementRef = useRef(null);
  const mergedInputRefs = mergeRefs([reorderPanelNodeRef, userRef]);
  /**
   * Announces the status when records are moved up/down
   */

  const makeAnnouncement = liveText => {
    setLiveText(liveText);
  };
  /**
   * Clears the announcement text
   */


  const clearAnnouncement = () => {
    setLiveText(null);
  };

  useEffect(() => {
    // Don't run on initial render.
    if (firstFocusUpdateRef.current) {
      firstFocusUpdateRef.current = false;
      return;
    }

    const topButton = topButtonRef.current;
    const bottomButton = bottomButtonRef.current;
    /**
     * This following logic sets the focus to the "moveToBottom" button when
     * the "moveToTop" button becomes disabled and similarly
     * the "moveToTop" button gets focused when the "moveToBottom" button becomes disabled.
     */

    if (reorderButtonTriggeredRef.current && bottomButton && disableMoveUpTopButtons) {
      bottomButton.focus();
    }

    if (reorderButtonTriggeredRef.current && topButton && disableMoveDownBottomButtons) {
      topButton.focus();
    }

    reorderButtonTriggeredRef.current = false;
  }, [disableMoveUpTopButtons, disableMoveDownBottomButtons]);
  /**
   * Handle closing the keyboard help panel.
   */

  const handleCloseKeyboardHelp = () => {
    if (activeElementRef.current) {
      onHandleCloseKeyboardHelp(true, activeElementRef.current);
      activeElementRef.current = null;
    } else {
      onHandleCloseKeyboardHelp(true, document.activeElement);
    }
  };
  /** Executed whenever `Clear` button is clicked. */


  const handleClearSelections = useCallback(() => {
    onClearSelections(FOCUS_TABLE_CELL);
  }, [onClearSelections]);
  /**
   * @param {array} selectedRows - array of selected row `rowKey` values.
   * Executes when selections are made.
   */

  const handleReorderSelect = _ref2 => {
    let {
      selectedRows
    } = _ref2;
    onReorderSelect({
      selectedRows
    });
  };
  /**
   * @param {string} action - to know if selectedRows need to be moved to TOP or BOTTOM.
   * Performs move to TOP/BOTTOM actions.
   * @param {boolean} focusTableCell - focuses the last focused cell after reorder.
   */


  const handleMoveTopBottom = useCallback((_ref3, focusTableCell) => {
    let {
      TOP
    } = _ref3;
    onMoveTopBottom(TOP && TOP, focusTableCell); // eslint-disable-next-line react/prop-types

    const {
      moveToTopAnnouncement,
      moveToBottomAnnouncement
    } = customStrings;
    const numberOfSelectedRows = reorderSelectedRows.length;
    const liveText = TOP ? moveToTopAnnouncement(numberOfSelectedRows) : moveToBottomAnnouncement(numberOfSelectedRows);
    makeAnnouncement(liveText);
    const reorderThroughKeyBoard = focusTableCell === true;
    /**
     * Below flag needs to be set to true only when reorder
     * triggered through reorder buttons click.
     */

    reorderButtonTriggeredRef.current = !reorderThroughKeyBoard && true;
  }, [customStrings, onMoveTopBottom, reorderSelectedRows.length]);
  /**
   * @param {string} action - to know if the selectedRows need to be moved UP or DOWN.
   * Performs move UP/DOWN actions in Reorder panel.
   * @param {boolean} focusTableCell - focuses the last focused cell after reorder.
   */

  const handleMoveUpDown = useCallback((_ref4, focusTableCell) => {
    let {
      UP
    } = _ref4;
    // eslint-disable-next-line react/prop-types
    const {
      moveUpAnnouncement,
      moveDownAnnouncement
    } = customStrings;
    const numberOfSelectedRows = reorderSelectedRows.length;
    onMoveUpDown(UP && UP, focusTableCell);
    const liveText = UP ? moveUpAnnouncement(numberOfSelectedRows) : moveDownAnnouncement(numberOfSelectedRows);
    makeAnnouncement(liveText);
    const reorderThroughKeyBoard = focusTableCell === true;
    /**
     * Below flag needs to be set to true only when reorder
     * triggered through reorder buttons click.
     */

    reorderButtonTriggeredRef.current = !reorderThroughKeyBoard && true;
  }, [customStrings, reorderSelectedRows.length, onMoveUpDown]);
  /**
   * Executed on click of the "Move Up" Icon button,
   * to move the row one level above it.
   * @param {boolean} focusTableCell - focuses the last focused cell after reorder.
   */

  const handleMoveUp = useCallback(focusTableCell => {
    handleMoveUpDown({
      UP
    }, focusTableCell);
  }, [handleMoveUpDown]);
  /**
   * Executed on click of the "Move To Top" Icon button,
   * to move the row to the top of the Table.
   * @param {boolean} focusTableCell - focuses the last focused cell after reorder.
   */

  const handleMoveToTop = useCallback(focusTableCell => {
    handleMoveTopBottom({
      TOP
    }, focusTableCell);
  }, [handleMoveTopBottom]);
  /** Executed whenever `Save` button is clicked. */

  const handleSave = () => {
    afterSave(REORDER);
  };
  /** Executed whenever `Close` or `Cancel` buttons are clicked. */


  const handleClose = () => {
    onClose(REORDER);
  };
  /** Executed whenever `Edit` button is clicked. */


  const handleEdit = () => {
    onEditOpen();
  };
  /**
   * Reorders the rows in Panel as per the keys.
   */


  const handleRowsReorder = useCallback((e, activeElementInTable) => {
    const {
      shiftKey,
      ctrlKey,
      altKey
    } = e;
    let key = e.key.toLowerCase();
    key = key.replace("arrow", "").replace("Spacebar", " ").toLowerCase();

    if (activeElementInTable) {
      switch (key) {
        case "x":
          e.preventDefault();
          shiftKey && handleClearSelections();
          break;

        case "up":
          if (shiftKey) {
            e.preventDefault();
            ctrlKey ? !topButtonRef.current.disabled && handleMoveToTop(FOCUS_TABLE_CELL) : !upButtonRef.current.disabled && handleMoveUp(FOCUS_TABLE_CELL);
          }

          break;

        case "down":
          if (shiftKey) {
            e.preventDefault();
            ctrlKey ? !bottomButtonRef.current.disabled && handleMoveTopBottom({}, FOCUS_TABLE_CELL) : !downButtonRef.current.disabled && handleMoveUpDown({}, FOCUS_TABLE_CELL);
          }

          break;

        default:
          break;
      }

      if (key === "∑" || altKey && key === "w") {
        e.preventDefault();
        makeAnnouncement(SELECTION_MODE_DISABLED);
        tableElementRef.current.focus();
      }
    } else if (key === "∑" || altKey && key === "w") {
      makeAnnouncement(SELECTION_MODE_ENABLED);
      handleTableCellFocus(true, reorderPanelNodeRef.current);
    }
  }, [handleMoveToTop, handleMoveUpDown, handleClearSelections, handleMoveTopBottom, handleMoveUp]);
  /**
   * Toggles the `KeyboardHelpPanel` visibility on ALT+k.
   * @param {Object} e - keydown event object.
   * @param {Boolean} activeElementInPanel - `true`, if the active element
   * is in the Panel.
   */

  const handleKeyboardHelpPanel = (e, activeElementInPanel) => {
    const key = e.key.toLowerCase();
    const {
      altKey
    } = e;

    if (key === KEYBOARD_KEYS.MAC_ALT_K || altKey && key === KEYBOARD_KEYS.K) {
      if (activeElementInPanel) {
        // Save current active element for use in handleCloseKeyboardHelp.
        activeElementRef.current = document.activeElement;
        keyBoardHelpButtonNodeRef.current.click();
      }
    }
  };
  /**
   * Handles the Keydown event on Reorder Panel.
   */


  const handleKeyDown = useCallback(e => {
    const {
      activeElement
    } = document;
    const activeElementInPanel = reorderPanelNodeRef.current.contains(activeElement);
    elementToFocusOnHelpPanelCloseRef.current = activeElement;
    const activeElementInTable = activeElement.tagName !== "TABLE" && reorderPanelNodeRef.current.querySelector("table").contains(activeElement);
    handleKeyboardHelpPanel(e, activeElementInPanel);
    handleRowsReorder(e, activeElementInTable);
  }, [handleRowsReorder]);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
  const {
    closeButtonAriaLabelReorderPanel,
    reorderPanelTitle,
    moveToTopAriaLabel,
    // eslint-disable-line react/prop-types
    moveUpAriaLabel,
    // eslint-disable-line react/prop-types
    moveDownArialLabel,
    // eslint-disable-line react/prop-types
    moveToBottomArialLabel,
    // eslint-disable-line react/prop-types
    saveButtonLabel,
    editButtonLabel,
    cancelButtonLabel,
    reorderPanelSelectedRowsMessage,
    clearSelectionAriaLabel,
    // eslint-disable-line react/prop-types
    clearSelectionDescription,
    // eslint-disable-line react/prop-types
    selectionControlAriaLabel,
    // eslint-disable-line react/prop-types
    moveToTopDescription,
    // eslint-disable-line react/prop-types
    moveUpDescription,
    // eslint-disable-line react/prop-types
    moveDownDescription,
    // eslint-disable-line react/prop-types
    moveToBottomDescription // eslint-disable-line react/prop-types

  } = customStrings;
  const numberOfSelectedRows = reorderSelectedRows.length;
  const selectedRowsMessage = reorderPanelSelectedRowsMessage(numberOfSelectedRows);
  let closeButton = null;

  if (numberOfSelectedRows) {
    closeButton = _jsxs(React.Fragment, {
      children: [_jsx(Button, {
        id: "reorderPanelClearSelectionButton",
        css: cssClearSelectionsButton,
        kind: "simple",
        "aria-label": clearSelectionAriaLabel,
        centerIcon: _jsx(IconClear, {
          size: "xsmall"
        }),
        onClick: handleClearSelections,
        "aria-describedby": clearSelectionDescriptionId,
        className: "pnr-reorder-panel__clear-selection-button"
      }), _jsx("span", {
        id: clearSelectionDescriptionId,
        className: "pnr-reorder-panel__clear-selection-desc",
        hidden: true,
        children: clearSelectionDescription
      })]
    });
  }

  const {
    ariaCaption,
    caption
  } = children.props; // Set help button icon according to theme.

  const HelpButtonIcon = helpIcon === "verify" ? IconVerify : IconHelp;
  return _jsxs(ListBuilderPanel, {
    customStrings: {
      closeButtonAriaLabel: closeButtonAriaLabelReorderPanel
    },
    open: openReorderPanel,
    width: panelWidth,
    title: reorderPanelTitle,
    elementToFocusOnClose: elementToFocusOnClose,
    className: "pnr-reorder-panel",
    css: cssRoot,
    onClose: handleClose,
    headerDockAriaLabel: reorderPanelHeaderDockAriaLabel,
    "aria-describedby": selectedCountId,
    ref: mergedInputRefs,
    headerDock: _jsxs(React.Fragment, {
      children: [_jsxs("div", {
        children: [_jsx("span", {
          className: "pnr-reorder-panel__selected-items",
          css: cssSelectedItems,
          id: selectedCountId,
          children: selectedRowsMessage
        }), closeButton]
      }), _jsx(LiveRegion, {
        className: "pnr-reorder-panel__selection-status",
        role: "status",
        announcementDelay: ANNOUNCEMENT_TIMEOUT,
        clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
        children: selectedRowsMessage
      }), _jsx("span", {
        id: keyboardHelpButtonDescriptionId,
        hidden: true,
        children: KEYBOARD_HELP_BTN_DESCRIPTION
      }), _jsx(LiveRegion, {
        className: "pnr-reorder-panel__reorder-status",
        politeness: "assertive",
        announcementDelay: ANNOUNCEMENT_TIMEOUT,
        clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
        onAnnouncementClear: clearAnnouncement,
        children: liveText
      }), _jsxs("div", {
        className: "pnr-reorder-panel__move-buttons-container",
        css: cssButtonsContainer,
        "aria-label": selectionControlAriaLabel,
        role: "toolbar",
        children: [_jsx("div", {
          className: "pnr-reorder-panel__help-button-container",
          css: cssKeyboardHelpButtonContainer,
          children: _jsx(Button, {
            id: "reorderPanelHelpButton",
            kind: "chromeless",
            className: "pnr-reorder-panel__key-board-help-button",
            css: cssKeyboardHelpButton,
            "aria-label": KEYBOARD_HELP_BUTTON,
            "aria-describedby": keyboardHelpButtonDescriptionId,
            ref: keyBoardHelpButtonNodeRef,
            onClick: handleCloseKeyboardHelp,
            centerIcon: _jsx(HelpButtonIcon, {
              size: "small",
              theme: "information"
            })
          })
        }), _jsx(Button, {
          id: "reorderPanelMoveToTopButton",
          disabled: disableMoveUpTopButtons,
          kind: "simple",
          "aria-label": moveToTopAriaLabel,
          centerIcon: _jsx(IconOrderTop, {
            size: iconSize
          }),
          className: "pnr-tooltip--top-right",
          onClick: handleMoveToTop,
          "aria-describedby": moveToTopDescriptionId,
          ref: topButtonRef
        }), _jsx(Button, {
          id: "reorderPanelMoveUpButton",
          disabled: disableMoveUpTopButtons,
          kind: "simple",
          "aria-label": moveUpAriaLabel,
          centerIcon: _jsx(IconOrderUp, {
            size: iconSize
          }),
          className: "pnr-tooltip--top-right",
          onClick: handleMoveUp,
          "aria-describedby": moveUpDescriptionId,
          ref: upButtonRef
        }), _jsx(Button, {
          id: "reorderPanelMoveDownButton",
          disabled: disableMoveDownBottomButtons,
          kind: "simple",
          "aria-label": moveDownArialLabel,
          centerIcon: _jsx(IconOrderDown, {
            size: iconSize
          }),
          className: "pnr-tooltip--top-right",
          onClick: handleMoveUpDown,
          "aria-describedby": moveDownDescriptionId,
          ref: downButtonRef
        }), _jsx(Button, {
          id: "reorderPanelMoveToBottomButton",
          disabled: disableMoveDownBottomButtons,
          kind: "simple",
          "aria-label": moveToBottomArialLabel,
          centerIcon: _jsx(IconOrderBottom, {
            size: iconSize
          }),
          className: "pnr-tooltip--top-right",
          onClick: handleMoveTopBottom,
          "aria-describedby": moveToBottomDescriptionId,
          ref: bottomButtonRef
        })]
      }), _jsx("span", {
        id: moveToTopDescriptionId,
        hidden: true,
        children: moveToTopDescription
      }), _jsx("span", {
        id: moveUpDescriptionId,
        hidden: true,
        children: moveUpDescription
      }), _jsx("span", {
        id: moveDownDescriptionId,
        hidden: true,
        children: moveDownDescription
      }), _jsx("span", {
        id: moveToBottomDescriptionId,
        hidden: true,
        children: moveToBottomDescription
      })]
    }),
    footer: _jsxs("div", {
      css: cssFooterButtons,
      children: [_jsx(Button, {
        id: "reorderPanelCancelButton",
        kind: "standard",
        label: cancelButtonLabel,
        css: cssCancelButton,
        onClick: handleClose
      }), !reorderPanelEditButtonHidden && _jsx(Button, {
        id: "reorderPanelEditButton",
        kind: "standard",
        label: editButtonLabel,
        css: cssEditButton,
        onClick: handleEdit,
        disabled: disableButtons.includes(EDIT)
      }), _jsx(Button, {
        id: "reorderPanelSaveButton",
        kind: "primary",
        label: saveButtonLabel,
        css: cssSaveButton,
        onClick: handleSave,
        disabled: disableButtons.includes(REORDERSAVE)
      })]
    }),
    children: [_jsx("div", {
      id: reorderTableCaptionId,
      className: "wf-u-visually-hidden",
      children: `${reorderTableName}. ${REORDER_INSTRUCTIONS}`
    }), /*#__PURE__*/React.cloneElement(children, {
      ariaCaption: ariaCaption || caption,
      caption: null,
      nonStickyHeader: true,
      totalRows: reorderPanelData.length,
      data: reorderData,
      expandable: null,
      sortable: null,
      selectable: _objectSpread(_objectSpread({}, children.props.selectable), {}, {
        selectedRows: reorderSelectedRows,
        selectionType: "row",
        selectAll: false,
        onSelectedChange: handleReorderSelect
      }),
      "aria-labelledby": reorderTableCaptionId,
      "aria-readonly": "true",
      "aria-multiselectable": "true",
      role: "grid",
      tabIndex: "-1",
      ref: tableElementRef
    })]
  });
});
ReorderPanel.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Callback to be executed after click of `Save` button. */
  afterSave: PropTypes.func.isRequired,

  /** This is what will be displayed inside the Panel. */
  children: PropTypes.node.isRequired,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /** aria-label for Panel's close button. */
    closeButtonAriaLabelReorderPanel: PropTypes.string,

    /** Title of Reorder panel. */
    reorderPanelTitle: PropTypes.string,

    /** Save button label that will be displayed in Edit and Reorder panels. */
    saveButtonLabel: PropTypes.string,

    /** Cancel button label that will be displayed in Edit and Reorder panels. */
    cancelButtonLabel: PropTypes.string,

    /** Edit button label. */
    editButtonLabel: PropTypes.string,

    /**
     * Render prop for the selected rows count message in the Reorder Panel
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `selectedRowsCount`: Number of rows selected in the Reorder Panel.
     *  </li>
     * </ul>
     */
    reorderPanelSelectedRowsMessage: PropTypes.func
  }).isRequired,

  /** Indicates if `move down` and `move to bottom` buttons should be enebaled or diabled. */
  disableMoveDownBottomButtons: PropTypes.bool.isRequired,

  /** Indicates if `move up` and `move to top` buttons should be enabled or disabled. */
  disableMoveUpTopButtons: PropTypes.bool.isRequired,

  /**
   * This prop identifies the element that should receive
   * focus after Reorder panel is closed.
   * It can be a css selector or a function which
   * returns a DOM node.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

  /** Executed when clear selections button is clicked. */
  onClearSelections: PropTypes.func.isRequired,

  /** Callback handler to be executed after click of `Close` or `Cancel` buttons. */
  onClose: PropTypes.func.isRequired,

  /** Handler used to open Edit panel. Executed when ever `Edit` button is clicked. */
  onEditOpen: PropTypes.func.isRequired,

  /**
   * A callback to toggle the keyboard help panel.
   * @ignore
   */
  onHandleCloseKeyboardHelp: PropTypes.func.isRequired,

  /** Executed when `move to top` or `move to bottom` buttons are clicked. */
  onMoveTopBottom: PropTypes.func.isRequired,

  /** Executed when `move up` or `move down` buttons are clicked. */
  onMoveUpDown: PropTypes.func.isRequired,

  /** Executed when selections are made in Reorder panel. */
  onReorderSelect: PropTypes.func.isRequired,

  /** Indicates if Reorder panel needs to be opened or closed. */
  openReorderPanel: PropTypes.bool.isRequired,

  /** Data that is displayed in Reorder Panel. */
  // eslint-disable-next-line react/forbid-prop-types
  reorderData: PropTypes.arrayOf(PropTypes.object).isRequired,

  /**
   * A function that generates a unique key for each item,
   * or a string that identifies the unique key among the items.
   */
  reorderPanelData: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,

  /** aria-label for Reorder Panel header dock. */
  reorderPanelHeaderDockAriaLabel: PropTypes.string.isRequired,

  /** An array of `id` values that indicate the corresponding data set is selected. */
  reorderSelectedRows: PropTypes.array.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * It will be read along with Instruction text for Reorder Table.
   */
  reorderTableName: PropTypes.string.isRequired,

  /**
   * Configuration to disable footer `Edit`, `Save` buttons.
   * @ignore
   */
  disableButtons: PropTypes.arrayOf(PropTypes.string),

  /** Width of the Reorder Panel. */
  panelWidth: PropTypes.string,

  /** Prop to hide the `edit` button inside the Reorder Panel. */
  reorderPanelEditButtonHidden: PropTypes.bool
} : {};
ReorderPanel.defaultProps = {
  panelWidth: null,
  reorderPanelEditButtonHidden: false,
  disableButtons: []
};
ReorderPanel.displayName = "ReorderPanel";
export default ReorderPanel;